import { Fragment, useMemo, useRef, useState } from 'react';
import { hasActivity, type DealV2 } from '../../types';
import { useStickyHeaderOffsets } from '../../hooks/useStickyHeaderOffsets';
import { colors, daysThresholds } from '../../styles/theme';
import { ACTIVITY_UNAVAILABLE_REASON } from '../../utils/activityCopy';
import { deepLinks } from '../../utils/deepLinks';
import { formatMoneyMm } from '../../utils/formatMoneyMm';
import { sortDealsV2, type SortDirection, type SortKey } from '../../utils/sortDealsV2';
import { ActivityIndicator, type ActivityMode } from '../shared/ActivityIndicator';
import { DealTypeBadge } from '../shared/DealTypeBadge';
import { PendingUpdateBadge } from '../shared/PendingUpdateBadge';
import { TeamDisplay } from '../shared/TeamDisplay';
import { ThemeTags } from '../shared/ThemeTags';
import styles from './DealTable.module.css';

/**
 * The v2 board's table — a duplicate of `DealTable` repiped to `DealV2` (DASH-2v).
 *
 * **Duplicated on purpose, and the duplicate is temporary by construction.** v1's copy is deleted at
 * the cut, when this becomes the Pipeline tab's table; that is the same replacement-beside decision
 * the backend fork already made, applied one layer up. The alternative — mapping v2 rows into `Deal`
 * so the existing table could be reused untouched — would put an `entry_id` in
 * `affinity_opportunity_id`, which feeds Affinity deep links and the Deal Updates cross-link, so
 * every such link would point at the wrong record.
 *
 * It reads as *simpler* than v1's table rather than longer, because of the branches v2 still does not
 * need: the `recently_decided` variants and v1's Affinity out-links. The stylesheet is shared, so the
 * two render identically. The "Pending Update" badge is one affordance the two boards share outright —
 * `PendingUpdateBadge`, one component, because a divergence there would read as a data difference
 * during parallel-run (v1's copy keeps its legacy /deals link and goes dark at T-2 — owner, 2026-08-06).
 *
 * **Activity is native as of DASH-2a 2a-4 tier 1, and it shares v1's `ActivityIndicator` outright** —
 * for the same reason as the badge, and because two renderers of one dataset is how the boards start
 * disagreeing for reasons that have nothing to do with the data. The column renders only when the
 * payload carries computed counts (`hasActivity`); otherwise it keeps the labelled-empty state, so "no
 * sparkline" reads as unavailable rather than broken. Two causes produce that state and the payload
 * cannot tell them apart — the backend read flag off, or a non-admin viewer of the firm-visible board
 * (the pre-parity numbers stay admin-only) — which is why the reason is one shared string
 * (`utils/activityCopy.ts`) rather than a sentence naming the flag.
 *
 * Reuses `DealTable.module.css` deliberately: a visual difference between v1 and v2 should mean the
 * DATA differs, never that someone restyled a cell.
 */

interface Props {
  deals: DealV2[];
  showSource?: boolean;
  sortKey: SortKey;
  sortDirection: SortDirection;
  onSortChange: (key: SortKey) => void;
  /** Partition by deal_type with header rows, as v1's board does. */
  groupByType?: boolean;
  /** Tint recently-changed rows and dim the rest, as v1's "Highlight Updates" does. */
  highlightUpdates?: boolean;
  /** Which activity figure the indicator shows — v1's `ActivityMode`, same semantics. */
  activityMode?: ActivityMode;
  /**
   * Board-wide bar scale for the weekly sparkline, computed on the page across every section
   * (`PipelineV2Page`), mode-aware. Threaded rather than derived here: a scale computed per table
   * would make each section's busiest deal draw at full height, so two sections' bars would mean
   * different numbers. Omitted → `ActivityIndicator` falls back to its own per-deal max.
   */
  activityGlobalMax?: number;
  /**
   * Partition into Active / Inactive with header rows, on v1's 7d predicate (`isDealActiveV2`).
   * Ignored when the payload carries no activity — the page disables the control in that case, and a
   * partition computed from absent counts would put every deal in "Inactive".
   */
  groupByActive?: boolean;
  /** Tint rows with nobody on the deal team, as v1 does in its origination sections. */
  isUnassignedRow?: (deal: DealV2) => boolean;
  /**
   * The CRM list these rows belong to — the `sel=list:<id>` half of the "Pending Update" badge's
   * panel link (badge spec §1.1). Absent → the badge renders disabled rather than linking at a list
   * it cannot name.
   */
  listId?: number | null;
}

/**
 * v1's row-highlight classification — now all three branches.
 *
 * v1 (`DealTable.classifyDealHighlight`) tests in order: the `new` badge, then either stage-move
 * badge, then `meeting_count_7d > 0`. The badges have been native since v2-b; the **meetings branch
 * is live as of 2a-4 tier 1**, and it is gated on `hasActivity` rather than on a truthy count —
 * without activity data a `0` default would silently reclassify every meeting-only deal as quiet,
 * which looks like the highlight working while it hides rows v1 would show.
 */
function classifyDealHighlight(deal: DealV2): 'new' | 'moved' | 'meetings' | null {
  if (deal.badges.includes('new')) return 'new';
  if (deal.badges.includes('moved') || deal.badges.includes('moved_back')) return 'moved';
  if (hasActivity(deal) && (deal.meeting_count_7d ?? 0) > 0) return 'meetings';
  return null;
}

/**
 * A deal is "active" if it was touched in the last 7 days — any 7d email or meeting, or a
 * new/moved/moved_back badge. Carried over verbatim from v1's `isDealActive`
 * (`utils/dealActivity.ts`, deleted 2026-09-02 with the v1 `Deal` type): it was reproduced rather
 * than shared because v1's took a `Deal` and this takes a `DealV2`, whose counts are nullable. This
 * is now the only version.
 */
export function isDealActiveV2(deal: DealV2): boolean {
  if ((deal.email_count_7d ?? 0) > 0) return true;
  if ((deal.meeting_count_7d ?? 0) > 0) return true;
  if (deal.badges.includes('moved')) return true;
  if (deal.badges.includes('moved_back')) return true;
  if (deal.badges.includes('new')) return true;
  return false;
}

const TYPE_BUCKETS: { key: string; label: string; match: (t: string) => boolean }[] = [
  { key: 'new', label: 'New', match: (t) => t === 'new' },
  { key: 'add-on', label: 'Add-On', match: (t) => t === 'add-on' },
  { key: 'ex-fund', label: 'New (ex-Fund)', match: (t) => t === 'new (ex-fund)' },
  { key: 'other', label: 'Other', match: () => true },
];

function bucketForDeal(deal: DealV2): string {
  const t = (deal.deal_type || '').toLowerCase();
  for (const b of TYPE_BUCKETS) {
    if (b.match(t)) return b.key;
  }
  return 'other';
}

// Same tokens and labels v1 uses, so a badge means the same thing on both boards. v2 emits only the
// three predicates it can compute natively — the terminal Passed/Lost/Hold/Closed swaps v1 does are
// tied to the recently_decided / recently_closed sections, which v2-a does not render.
const badgeColors: Record<string, { bg: string; color: string; border: string }> = {
  new: colors.badge.new,
  moved: colors.badge.moved,
  moved_back: colors.badge.moved_back,
};

const badgeLabels: Record<string, string> = {
  new: 'NEW',
  moved: '↑ MOVED',
  moved_back: '↓ MOVED',
};

function daysColor(days: number | null): string {
  if (days === null) return '#666';
  if (days > daysThresholds.danger) return '#C62828';
  if (days > daysThresholds.warning) return '#E65100';
  return '#666';
}

function SortTh({
  label,
  sortField,
  currentKey,
  direction,
  onSort,
  style,
}: {
  label: string;
  sortField: SortKey;
  currentKey: SortKey;
  direction: SortDirection;
  onSort: (key: SortKey) => void;
  style?: React.CSSProperties;
}) {
  const active = currentKey === sortField;
  return (
    <th
      className={`${styles.th} ${styles.sortable}`}
      style={style}
      onClick={() => onSort(sortField)}
    >
      {label}
      {active && (
        <span className={styles.sortArrow}>
          {direction === 'asc' ? ' ▲' : ' ▼'}
        </span>
      )}
    </th>
  );
}

export function DealTableV2({
  deals,
  showSource = false,
  sortKey,
  sortDirection,
  onSortChange,
  groupByType = false,
  highlightUpdates = false,
  isUnassignedRow,
  listId,
  activityMode = 'total',
  activityGlobalMax,
  groupByActive = false,
}: Props) {
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  useStickyHeaderOffsets(tableRef);

  // Whether the payload carries computed counts at all. Read off the FIRST deal because the backend
  // populates the fields for every row or none of them — it is one flag on one read, not a per-deal
  // property — so this is a payload-shape question, not a data question.
  const activityPresent = deals.length > 0 && hasActivity(deals[0]);

  const sortedDeals = useMemo(() => {
    let inner = sortDealsV2(deals, sortKey, sortDirection);
    // Type partition (inner-most), then the active/inactive partition wrapping it — v1's stacking
    // order, so a board with both toggles on bands identically on the two tabs.
    if (groupByType) {
      const buckets = new Map<string, DealV2[]>();
      for (const b of TYPE_BUCKETS) buckets.set(b.key, []);
      for (const d of inner) buckets.get(bucketForDeal(d))!.push(d);
      inner = TYPE_BUCKETS.flatMap((b) => buckets.get(b.key)!);
    }
    if (!groupByActive || !activityPresent) return inner;
    const active: DealV2[] = [];
    const inactive: DealV2[] = [];
    for (const d of inner) {
      if (isDealActiveV2(d)) active.push(d);
      else inactive.push(d);
    }
    return [...active, ...inactive];
  }, [deals, sortKey, sortDirection, groupByType, groupByActive, activityPresent]);

  // Counted off `sortedDeals`, as v1 does, so the two headers report the same number.
  const activeCount = useMemo(
    () => (groupByActive && activityPresent ? sortedDeals.filter(isDealActiveV2).length : 0),
    [sortedDeals, groupByActive, activityPresent],
  );
  const inactiveCount = sortedDeals.length - activeCount;

  const typeCounts = useMemo(() => {
    if (!groupByType) return {} as Record<string, number>;
    const counts: Record<string, number> = {};
    for (const b of TYPE_BUCKETS) counts[b.key] = 0;
    for (const d of deals) counts[bucketForDeal(d)]++;
    return counts;
  }, [deals, groupByType]);
  const typeLabelByKey: Record<string, string> = Object.fromEntries(
    TYPE_BUCKETS.map((b) => [b.key, b.label]),
  );

  return (
    <table ref={tableRef} className={styles.table}>
      <thead>
        <tr className={styles.headerRow}>
          <SortTh label="Company" sortField="company" currentKey={sortKey} direction={sortDirection} onSort={onSortChange} style={{ width: '28%' }} />
          <SortTh label={showSource ? 'Source' : 'Status'} sortField="stage" currentKey={sortKey} direction={sortDirection} onSort={onSortChange} style={{ width: '18%' }} />
          <th className={styles.th} style={{ width: '5%', textAlign: 'center' }}>Rev</th>
          <th className={styles.th} style={{ width: '5%', textAlign: 'center' }}>EBITDA</th>
          <SortTh label="EV" sortField="ev" currentKey={sortKey} direction={sortDirection} onSort={onSortChange} style={{ width: '5%', textAlign: 'center' }} />
          <SortTh label="Est. EI Inv." sortField="est_investment" currentKey={sortKey} direction={sortDirection} onSort={onSortChange} style={{ width: '5%', textAlign: 'center' }} />
          {/* Native and attributed when the payload carries counts (the backend read flag on AND an
              admin caller — the board is firm-visible, the pre-parity numbers are not); still labelled
              when it does not, so an empty column reads as unavailable rather than broken. */}
          {activityPresent ? (
            <th
              className={styles.th}
              style={{ width: '20%' }}
              title="Weekly meetings + emails over the last 8 weeks, attributed to this deal from our own mail and calendar — not Affinity. Quiet deals show the 7-day counts instead. Pre-parity: the DASH-2a parity artifact is not signed yet."
            >
              Activity <span className={styles.sortArrow}>(pre-parity)</span>
            </th>
          ) : (
            <th className={styles.th} style={{ width: '20%' }} title={ACTIVITY_UNAVAILABLE_REASON}>
              Activity <span className={styles.sortArrow}>(unavailable)</span>
            </th>
          )}
          <th className={styles.th} style={{ width: '14%' }}>Deal Team</th>
        </tr>
      </thead>
      <tbody>
        {sortedDeals.map((deal, idx) => {
          const isExpanded = expandedId === deal.entry_id;

          const partitioning = groupByActive && activityPresent;
          const dealIsActive = partitioning ? isDealActiveV2(deal) : false;
          const prevIsActive = idx > 0 && partitioning ? isDealActiveV2(sortedDeals[idx - 1]) : false;
          const showActiveHeader = partitioning && (idx === 0 || prevIsActive !== dealIsActive);

          const prevDeal = idx > 0 ? sortedDeals[idx - 1] : null;
          const leadChanged =
            !prevDeal || (deal.deal_lead_name ?? '') !== (prevDeal.deal_lead_name ?? '');
          // v1 repeats the lead header at an active/inactive boundary, so the partition does not
          // swallow the band for the lead that straddles it.
          const activeBoundary = partitioning && idx > 0 && prevIsActive !== dealIsActive;
          const showGroupHeader =
            sortKey === 'lead' && (idx === 0 || leadChanged || activeBoundary);

          const dealBucket = groupByType ? bucketForDeal(deal) : '';
          const prevBucket = groupByType && prevDeal ? bucketForDeal(prevDeal) : '';
          const showTypeHeader = groupByType && (idx === 0 || dealBucket !== prevBucket);

          // Same precedence v1 uses: a highlight tint wins over the unassigned tint, and every
          // non-highlighted row is dimmed so the highlighted ones read as the answer to "what moved".
          const highlight = highlightUpdates ? classifyDealHighlight(deal) : null;
          const isQuiet = highlightUpdates && !highlight;
          const hlStyle = highlight ? colors.highlight[highlight] : null;
          const isUnassigned = isUnassignedRow?.(deal) ?? false;
          const rowBg = hlStyle ? hlStyle.bg : isUnassigned ? colors.origUnassigned : 'transparent';
          const rowClassName = [styles.row, styles.clickableRow, isQuiet ? styles.quietRow : '']
            .filter(Boolean)
            .join(' ');

          return (
            <Fragment key={deal.entry_id}>
              {showActiveHeader && (
                <tr className={styles.groupHeaderRow}>
                  <td colSpan={8} className={styles.groupHeaderCell}>
                    {dealIsActive ? `Active (${activeCount})` : `Inactive (${inactiveCount})`}
                  </td>
                </tr>
              )}
              {showTypeHeader && (
                <tr className={styles.groupHeaderRow}>
                  <td colSpan={8} className={styles.groupHeaderCell}>
                    {typeLabelByKey[dealBucket]} ({typeCounts[dealBucket] ?? 0})
                  </td>
                </tr>
              )}
              {showGroupHeader && deal.deal_lead_name && (
                <tr className={styles.groupHeaderRow}>
                  <td colSpan={8} className={styles.groupHeaderCell}>
                    {deal.deal_lead_name}
                  </td>
                </tr>
              )}
              <tr
                className={rowClassName}
                style={{ background: rowBg }}
                onClick={() => setExpandedId(isExpanded ? null : deal.entry_id)}
              >
                {/* Company. No Affinity out-link: this board is the thing that replaces it — the
                    name goes to the CRM record instead, which is where that record now lives.

                    A real <a> (not an onClick handler) so middle-click / ⌘-click / "open in new tab"
                    all behave, and `_blank` because the board is the Monday review surface: opening
                    the record in place would cost you your scroll position and your filters. That is
                    the one deliberate difference from `PendingUpdateBadge`, which navigates in place.
                    `rel` is not optional on a `_blank` target — without `noopener` the opened page
                    gets a handle on this window via `window.opener`.

                    Guarded on `listId` exactly as the badge is: `/crm?sel=list:null` would be a link
                    at a list we cannot name, so an absent list renders the plain span instead of a
                    broken href. `/api/crm/board-v2` always sets `list_id` in production; the null is
                    the FE type's, and the page-level test fixture's. */}
                <td className={styles.td}>
                  <div className={styles.companyLine}>
                    {listId != null ? (
                      <a
                        className={`${styles.companyName} ${styles.companyNameLink}`}
                        href={deepLinks.crmRecord(listId, deal.entry_id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Open the deal record in the CRM panel (new tab)"
                        aria-label={`Open ${deal.name} in the CRM record panel (new tab)`}
                        // stopPropagation: the row itself is clickable (expand), and a name click is
                        // a link click, not a row click — otherwise following the link would also
                        // rearrange the board behind the tab you just opened. Expansion stays
                        // reachable from every other cell, which is how it already worked.
                        onClick={(e) => e.stopPropagation()}
                      >
                        {deal.name}
                      </a>
                    ) : (
                      <span className={styles.companyName}>{deal.name}</span>
                    )}
                    <DealTypeBadge type={deal.deal_type} />
                    {deal.badges.map((b) => {
                      const bc = badgeColors[b];
                      if (!bc) return null;
                      return (
                        <span
                          key={b}
                          className={styles.badge}
                          style={{ background: bc.bg, color: bc.color, border: `1px solid ${bc.border}` }}
                        >
                          {badgeLabels[b] || b}
                        </span>
                      );
                    })}
                    {deal.pending_count > 0 && (
                      // Native "Pending Update" (badge spec §1.1) — opens the CRM record panel, where
                      // proposals are reviewed now. `listId` is the board's own list, threaded from the
                      // page rather than guessed: this table renders one list at a time and the panel
                      // link is meaningless without it.
                      <PendingUpdateBadge
                        dealName={deal.name}
                        listId={listId}
                        entryId={deal.entry_id}
                        clickable={deal.pending_clickable}
                      />
                    )}
                  </div>
                  <ThemeTags themes={deal.themes} />
                  <div className={styles.companySubline}>
                    {[deal.sourcing_type, ...deal.financial_advisor]
                      .filter(Boolean)
                      .join(' · ') || ' '}
                  </div>
                </td>

                {/* Status + days in stage (from the history union, not the entry) */}
                <td className={styles.td}>
                  <span className={styles.stageLabel}>
                    {showSource ? deal.sourcing_channel || deal.sourcing_type || '' : deal.stage}
                  </span>
                  {!showSource && deal.days_in_stage !== null && (
                    <div
                      className={styles.statusSubline}
                      style={{ color: daysColor(deal.days_in_stage) }}
                    >
                      {deal.days_in_stage} {deal.days_in_stage === 1 ? 'day' : 'days'} in stage
                    </div>
                  )}
                </td>

                <td className={styles.td} style={{ textAlign: 'center' }}>
                  {formatMoneyMm(deal.revenue)}
                </td>
                <td className={styles.td} style={{ textAlign: 'center' }}>
                  {formatMoneyMm(deal.ebitda)}
                </td>
                <td className={styles.td} style={{ textAlign: 'center' }}>
                  {formatMoneyMm(deal.enterprise_value)}
                </td>
                <td className={styles.td} style={{ textAlign: 'center', fontWeight: 700 }}>
                  {formatMoneyMm(deal.est_investment)}
                </td>

                {/* Activity: the shared indicator, now with the weekly series, so the cell draws
                    v1's 8-week sparkline (DASH-2b W3, spec §5).

                    `lost_passed_category` is deliberately NOT shown here, a divergence from v1's
                    "decided deal shows its category" precedence. Lost/Passed/On Hold map to the
                    hidden section, which this board never renders, so every row here is an active
                    deal; a category on one is a leftover from before the deal was re-activated (the
                    field is not cleared on revival) and would read as if the deal were dead.

                    `activity_weeks` is a THREE-state field and the ladder here is the whole contract:
                    absent/null = the read flag is off (this branch is not reached, the column is
                    labelled not-yet-native); `[]` = computed and all eight weeks were zero, which
                    falls to the indicator's collapsed "meetings / emails" form exactly as v1's
                    drop-if-silent does; eight entries = the chart. `?? []` is therefore the right
                    default, not a papering-over.

                    The series comes from `dash_deal_weekly`, a per-DEAL table counted once per item
                    in the same producer pass as these counts (#957/#960). It is never summed out of
                    the per-person weekly table, which increments once per internal participant and so
                    would not add up to the counts beside it.

                    `activityStale` is deliberately not passed: v2's producer is a uniform batch, so
                    v1's per-deal staleness shimmer has nothing to reflect (spec §5, named divergence). */}
                <td className={styles.td}>
                  {activityPresent ? (
                    <ActivityIndicator
                      emails7d={deal.email_count_7d ?? 0}
                      emails14d={deal.email_count_14d ?? 0}
                      emails30d={deal.email_count_30d ?? 0}
                      meetings7d={deal.meeting_count_7d ?? 0}
                      meetings14d={deal.meeting_count_14d ?? 0}
                      meetings30d={deal.meeting_count_30d ?? 0}
                      activityWeeks={deal.activity_weeks ?? []}
                      globalMax={activityGlobalMax}
                      mode={activityMode}
                    />
                  ) : (
                    <span className={styles.companySubline}>—</span>
                  )}
                </td>

                <td className={styles.td}>
                  <TeamDisplay members={deal.deal_team} />
                </td>
              </tr>

              {isExpanded && (
                <tr key={`${deal.entry_id}-expanded`} className={styles.expandedRow}>
                  <td colSpan={8}>
                    <div className={styles.expandedContent}>
                      <div className={styles.expandedGrid}>
                        <div className={styles.expandedSection}>
                          <span className={styles.detailsLabel}>Description</span>
                          <span className={styles.detailsText}>
                            {deal.opportunity_description || 'No description available'}
                          </span>
                        </div>
                        <div className={styles.expandedSection}>
                          <span className={styles.detailsLabel}>Next Steps</span>
                          <span className={styles.detailsText}>
                            {deal.next_steps || 'No next steps defined'}
                          </span>
                        </div>
                      </div>
                      <div className={styles.financialRow}>
                        <div className={styles.financialItem}>
                          <span className={styles.financialLabel}>Revenue</span>
                          <span className={styles.financialValue}>{formatMoneyMm(deal.revenue)}</span>
                        </div>
                        <div className={styles.financialItem}>
                          <span className={styles.financialLabel}>EBITDA</span>
                          <span className={styles.financialValue}>{formatMoneyMm(deal.ebitda)}</span>
                        </div>
                        <div className={styles.financialItem}>
                          <span className={styles.financialLabel}>EV</span>
                          <span className={styles.financialValue}>
                            {formatMoneyMm(deal.enterprise_value)}
                          </span>
                        </div>
                        {/* Organization shares this row with the three figures but is PROSE, so it
                            takes the same modifier Contacts does — a company name set at the
                            figures' 16px/700 read as a heading (owner, 2026-08-10). */}
                        <div className={styles.financialItem}>
                          <span className={styles.financialLabel}>Organization</span>
                          <span className={`${styles.financialValue} ${styles.financialValueProse}`}>
                            {deal.organization_name || '—'}
                          </span>
                        </div>
                      </div>
                      {/* Who the linked contacts are. The "Team source" line that sat beside this one
                          is gone (owner, 2026-08-09): it was provenance for the parallel-run reviewer
                          — whether the team came from the hand-entered column — and it reads as noise
                          on the board itself. Only the RENDER went: `deal_team_source` is still
                          produced and still lands in the parity artifact (`crm/board_parity.py`).

                          `financialValueProse` because this is a list of names, not a figure — see the
                          stylesheet. */}
                      <div className={styles.financialRow}>
                        <div className={styles.financialItem}>
                          <span className={styles.financialLabel}>Contacts</span>
                          <span className={`${styles.financialValue} ${styles.financialValueProse}`}>
                            {deal.people.length > 0 ? deal.people.join(', ') : '—'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </Fragment>
          );
        })}
      </tbody>
    </table>
  );
}
